import { useState, useEffect, useCallback, useRef } from "react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import { useSelector, useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import {
  selectTherapistProfile,
  setTherapistProfile,
} from "../../store/slices/authSlice";
import {
  useInitGoogleCalendarSyncMutation,
  useResyncGoogleCalendarMutation,
  useDisconnectGoogleCalendarMutation,
} from "../../store/services/calendarApi";
import { useToast } from "../../contexts/ToastContext";
import {
  ArrowsClockwise,
  CheckCircle,
  ArrowSquareOut,
  Shield,
  CalendarCheck,
  ArrowRight,
  WarningCircle,
  LinkBreak,
} from "@phosphor-icons/react";
import ModalCloseButton from "../ui/ModalCloseButton";
import GlobalLoader from "../shared/GlobalLoader";
import ConfirmDialog from "../ui/ConfirmDialog";
import gLogo from "../../assets/g-logo.png";
import "./GoogleCalendarSync.css";

const GOOGLE_CLIENT_ID =
  "35158995981-dmnnhqk6oa53ojebocba6rjthon5vak6.apps.googleusercontent.com";

/**
 * Detect whether a Firebase Functions error from initSync/resync indicates
 * that the user revoked our app's Google access (or the connection was
 * already cleared on the backend).
 */
function isGoogleRevokedError(error) {
  if (!error) return false;
  // RTK Query queryFn shape (we forward { code, message, details })
  if (error?.details?.reason === "revoked") return true;
  if (error?.details?.reason === "not-connected") return true;
  if (typeof error?.message === "string") {
    if (error.message.includes("google-revoked")) return true;
    if (error.message.includes("google-not-connected")) return true;
  }
  return false;
}

function GoogleCalendarSync() {
  const dispatch = useDispatch();
  const therapistProfile = useSelector(selectTherapistProfile);
  const { showError, showSuccess } = useToast();

  const [initSync] = useInitGoogleCalendarSyncMutation();
  const [resync] = useResyncGoogleCalendarMutation();
  const [disconnect] = useDisconnectGoogleCalendarMutation();

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncLoaderMessage, setSyncLoaderMessage] = useState("");
  const [successInfo, setSuccessInfo] = useState(null);
  const [revokedInfo, setRevokedInfo] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  // Tracks whether we are in the middle of an OAuth flow. Prevents the
  // Google `error_callback` (which sometimes fires with type `popup_closed`
  // even when the auth code IS about to arrive in `callback`) from
  // prematurely tearing down the loader.
  const oauthInFlightRef = useRef(false);
  // Fallback timer that re-arms the loader if `error_callback` fires but
  // `callback` arrives shortly after.
  const oauthSettleTimerRef = useRef(null);

  const isConnected =
    therapistProfile?.googleCalendarSync?.isConnected === true;
  const syncEmail = therapistProfile?.googleCalendarSync?.email;

  useEffect(() => {
    const shouldLockScroll =
      showConfirmPopup || successInfo !== null || revokedInfo !== null;
    if (shouldLockScroll) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showConfirmPopup, successInfo, revokedInfo]);

  useEffect(() => {
    return () => {
      if (oauthSettleTimerRef.current) {
        clearTimeout(oauthSettleTimerRef.current);
      }
    };
  }, []);

  /**
   * Mark the connection as disconnected in Redux. The backend already
   * cleared the Firestore record (either by `disconnect` callable or by
   * detecting `invalid_grant`).
   */
  const markDisconnectedInRedux = useCallback(() => {
    if (!therapistProfile) return;
    dispatch(
      setTherapistProfile({
        ...therapistProfile,
        googleCalendarSync: {
          ...(therapistProfile.googleCalendarSync || {}),
          isConnected: false,
          disconnectedAt: new Date().toISOString(),
        },
      }),
    );
  }, [dispatch, therapistProfile]);

  const startGoogleOAuth = useCallback(() => {
    if (!window.google?.accounts?.oauth2) {
      showError("שגיאה בטעינת Google. נסה לרענן את הדף.");
      return;
    }

    const settleAsCancelled = () => {
      // If `callback` did not arrive within a short window, treat the OAuth
      // flow as cancelled by the user and tear down the loader.
      if (!oauthInFlightRef.current) return;
      oauthInFlightRef.current = false;
      setIsSyncing(false);
      setSyncLoaderMessage("");
    };

    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: GOOGLE_CLIENT_ID,
      scope:
        "https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/userinfo.email",
      error_callback: (err) => {
        // GIS sometimes fires this with `type: 'popup_closed'` even though
        // `callback` is about to be invoked with a valid code. Wait briefly
        // before tearing down the loader; if `callback` arrives in the
        // meantime, it will clear the timer and keep the loader visible.
        console.warn("[CK-9400] Google OAuth error_callback:", err);
        if (oauthSettleTimerRef.current) {
          clearTimeout(oauthSettleTimerRef.current);
        }
        oauthSettleTimerRef.current = setTimeout(settleAsCancelled, 1500);
      },
      callback: async (response) => {
        // `callback` arrived — cancel any pending settle-as-cancelled timer
        // queued by `error_callback`.
        if (oauthSettleTimerRef.current) {
          clearTimeout(oauthSettleTimerRef.current);
          oauthSettleTimerRef.current = null;
        }
        oauthInFlightRef.current = false;

        if (response.error) {
          console.error("[CK-9400] Google OAuth error:", response.error);
          setIsSyncing(false);
          setSyncLoaderMessage("");
          if (response.error !== "access_denied") {
            showError("שגיאה בחיבור לחשבון Google");
          }
          return;
        }

        try {
          // Make sure the loader is up (in case `error_callback` raced
          // ahead and cleared it before this callback ran).
          setShowConfirmPopup(false);
          setRevokedInfo(null);
          setIsSyncing(true);
          setSyncLoaderMessage("מסנכרן יומן");
          const result = await initSync({ code: response.code }).unwrap();

          if (therapistProfile) {
            dispatch(
              setTherapistProfile({
                ...therapistProfile,
                googleCalendarSync: {
                  ...(therapistProfile.googleCalendarSync || {}),
                  isConnected: true,
                  calendarId: result.calendarId,
                  email: result.email,
                  connectedAt:
                    therapistProfile.googleCalendarSync?.connectedAt ||
                    new Date().toISOString(),
                  lastSyncAt: new Date().toISOString(),
                },
              }),
            );
          }

          setShowConfirmPopup(false);
          setSuccessInfo({
            email: result.email,
            syncedCount: result.syncedCount || 0,
            mode: "init",
          });
          trackFeature(FEATURE_KEYS.GOOGLE_CALENDAR_SYNC);
        } catch (error) {
          console.error("[CK-9400] Error initializing sync:", error);
          showError("שגיאה בסנכרון היומן. נסה שוב.");
        } finally {
          setIsSyncing(false);
          setSyncLoaderMessage("");
        }
      },
    });

    // CRITICAL: close the consent popup and turn on the full-screen loader
    // BEFORE Google's popup opens. This guarantees that there is never a
    // moment where the user sees the (now-stale) "Continue with Google"
    // popup without any loading indicator on top.
    setShowConfirmPopup(false);
    setRevokedInfo(null);
    setSyncLoaderMessage("מתחבר לחשבון Google...");
    setIsSyncing(true);
    oauthInFlightRef.current = true;
    if (oauthSettleTimerRef.current) {
      clearTimeout(oauthSettleTimerRef.current);
      oauthSettleTimerRef.current = null;
    }

    client.requestCode();
  }, [initSync, showError, dispatch, therapistProfile]);

  const handleResync = useCallback(async () => {
    try {
      setSyncLoaderMessage("מסנכרן יומן");
      setIsSyncing(true);
      const result = await resync().unwrap();

      if (therapistProfile) {
        dispatch(
          setTherapistProfile({
            ...therapistProfile,
            googleCalendarSync: {
              ...(therapistProfile.googleCalendarSync || {}),
              lastSyncAt: new Date().toISOString(),
            },
          }),
        );
      }

      setSuccessInfo({
        email: therapistProfile?.googleCalendarSync?.email || "",
        syncedCount: result.syncedCount || 0,
        mode: "resync",
      });
    } catch (error) {
      console.error("[CK-9401] Error resyncing:", error);

      if (isGoogleRevokedError(error)) {
        // Backend already cleared the connection — sync local Redux state
        // and show a friendly explanation + reconnect CTA.
        markDisconnectedInRedux();
        setRevokedInfo({
          previousEmail: therapistProfile?.googleCalendarSync?.email || "",
        });
      } else {
        showError("שגיאה בסנכרון מחדש. נסה שוב.");
      }
    } finally {
      setIsSyncing(false);
      setSyncLoaderMessage("");
    }
  }, [resync, showError, dispatch, therapistProfile, markDisconnectedInRedux]);

  const handleDisconnect = useCallback(async () => {
    try {
      setSyncLoaderMessage("מנתק יומן");
      setIsSyncing(true);
      await disconnect().unwrap();
      markDisconnectedInRedux();
      showSuccess("החיבור ליומן Google נותק בהצלחה");
    } catch (error) {
      console.error("[CK-9402] Error disconnecting:", error);
      // Even if the server call failed, clearing locally is safer than
      // leaving the user stuck — but only on revoked-style errors.
      if (isGoogleRevokedError(error)) {
        markDisconnectedInRedux();
        showSuccess("החיבור ליומן Google נותק");
      } else {
        showError("שגיאה בניתוק החיבור. נסה שוב.");
      }
    } finally {
      setIsSyncing(false);
      setSyncLoaderMessage("");
    }
  }, [disconnect, markDisconnectedInRedux, showSuccess, showError]);

  const handleReconnectFromRevoked = () => {
    setRevokedInfo(null);
    setShowConfirmPopup(true);
  };

  const handleCloseConfirm = () => setShowConfirmPopup(false);
  const handleCloseSuccess = () => setSuccessInfo(null);
  const handleCloseRevoked = () => setRevokedInfo(null);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleCloseConfirm();
  };

  const handleSuccessOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleCloseSuccess();
  };

  const handleRevokedOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleCloseRevoked();
  };

  return (
    <div className="google-calendar-sync">
      {isConnected ? (
        <div className="gcal-card gcal-card-connected">
          <div className="gcal-connected-status">
            <span className="gcal-status-indicator">
              <span className="gcal-status-dot" />
              <CheckCircle size={14} weight="fill" />
              <span>מחובר ומסונכרן</span>
            </span>
          </div>

          <div className="gcal-account-row">
            <img src={gLogo} alt="Google" className="gcal-logo" />
            <div className="gcal-account-info">
              <span className="gcal-account-label">חשבון מסונכרן</span>
              <span className="gcal-account-email" dir="ltr">
                {syncEmail || "—"}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="gcal-primary-btn"
            onClick={handleResync}
            disabled={isSyncing}
          >
            <ArrowsClockwise size={16} weight="bold" />
            <span>סנכרן מחדש</span>
          </button>

          <button
            type="button"
            className="gcal-disconnect-btn"
            onClick={() => setShowDisconnectConfirm(true)}
            disabled={isSyncing}
          >
            <LinkBreak size={14} weight="bold" />
            <span>נתק את החיבור ליומן Google</span>
          </button>

          <a
            className="gcal-disconnect-link"
            href="https://myaccount.google.com/permissions"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>לניהול ההרשאות בחשבון Google</span>
            <ArrowSquareOut size={13} weight="bold" />
          </a>
        </div>
      ) : (
        <div className="gcal-card gcal-card-empty">
          <div className="gcal-logo-wrap">
            <img src={gLogo} alt="Google" className="gcal-logo-hero" />
          </div>

          <div className="gcal-empty-copy">
            <h4 className="gcal-empty-title">היומן לא מחובר</h4>
            <p className="gcal-empty-subtitle">
              חבר את חשבון Google שלך כדי שפגישות CliniKit יופיעו אוטומטית
              ביומן Google ייעודי בשם <strong>CLINIKIT</strong>.
            </p>
          </div>

          <button
            type="button"
            className="gcal-primary-btn gcal-primary-btn-lg"
            onClick={() => setShowConfirmPopup(true)}
          >
            <img src={gLogo} alt="" className="gcal-btn-logo" />
            <span>חבר חשבון Google</span>
          </button>
        </div>
      )}

      {showConfirmPopup &&
        !isSyncing &&
        createPortal(
          <div className="gcal-popup-overlay" onClick={handleOverlayClick}>
            <div className="gcal-popup" onClick={(e) => e.stopPropagation()}>
              <div className="gcal-popup-close-wrap">
                <ModalCloseButton onClick={handleCloseConfirm} />
              </div>

              <div className="gcal-popup-logo-wrap">
                <img src={gLogo} alt="Google" className="gcal-popup-logo" />
              </div>

              <h3 className="gcal-popup-title">סנכרון עם Google Calendar</h3>
              <p className="gcal-popup-sub">
                אחרי ההתחברות, כל פגישות CliniKit יסתנכרנו אוטומטית ליומן
                ייעודי בחשבון Google שלך.
              </p>

              <ul className="gcal-benefits">
                <li>
                  <span className="gcal-benefit-icon">
                    <CalendarCheck size={16} weight="bold" />
                  </span>
                  <div className="gcal-benefit-text">
                    <strong>יומן ייעודי נפרד</strong>
                    <span>ייווצר יומן חדש בשם CLINIKIT שלא יעורבב עם שאר היומנים שלך</span>
                  </div>
                </li>
                <li>
                  <span className="gcal-benefit-icon">
                    <ArrowRight size={16} weight="bold" />
                  </span>
                  <div className="gcal-benefit-text">
                    <strong>סנכרון חד-צדדי</strong>
                    <span>רק מ-CliniKit ל-Google. פגישות ביומן Google לא ישפיעו על CliniKit</span>
                  </div>
                </li>
                <li>
                  <span className="gcal-benefit-icon">
                    <Shield size={16} weight="bold" />
                  </span>
                  <div className="gcal-benefit-text">
                    <strong>עדכונים אוטומטיים</strong>
                    <span>כל שינוי, ביטול או פגישה חדשה יתעדכנו מיידית</span>
                  </div>
                </li>
              </ul>

              <button
                type="button"
                className="gcal-primary-btn gcal-primary-btn-lg gcal-popup-cta"
                onClick={startGoogleOAuth}
                disabled={isSyncing}
              >
                <img src={gLogo} alt="" className="gcal-btn-logo" />
                <span>המשך עם Google</span>
              </button>

              <p className="gcal-popup-note">
                אפשר לבטל את החיבור בכל רגע מתוך CliniKit או דרך הגדרות חשבון
                Google.
              </p>
            </div>
          </div>,
          document.body,
        )}

      {/* Full-screen loader while exchanging the OAuth code & syncing */}
      {isSyncing && <GlobalLoader message={syncLoaderMessage || "מסנכרן יומן"} />}

      {/* Success popup after sync completes */}
      {successInfo &&
        createPortal(
          <div
            className="gcal-popup-overlay gcal-success-overlay"
            onClick={handleSuccessOverlayClick}
          >
            <div className="gcal-popup gcal-success-popup" onClick={(e) => e.stopPropagation()}>
              <div className="gcal-popup-close-wrap">
                <ModalCloseButton onClick={handleCloseSuccess} />
              </div>

              <div className="gcal-success-icon-wrap">
                <CheckCircle size={64} weight="fill" />
              </div>

              <h3 className="gcal-popup-title">היומן סונכרן בהצלחה!</h3>
              <p className="gcal-popup-sub">
                {successInfo.mode === "resync"
                  ? "כל הפגישות עודכנו ביומן Google שלך."
                  : "החיבור הושלם וכל הפגישות הועברו ליומן Google ייעודי בשם CLINIKIT."}
              </p>

              <ul className="gcal-success-stats">
                {successInfo.email && (
                  <li>
                    <span className="gcal-success-stat-label">חשבון מחובר</span>
                    <span className="gcal-success-stat-value" dir="ltr">
                      {successInfo.email}
                    </span>
                  </li>
                )}
                <li>
                  <span className="gcal-success-stat-label">
                    {successInfo.mode === "resync"
                      ? "פגישות שעודכנו"
                      : "פגישות שסונכרנו"}
                  </span>
                  <span className="gcal-success-stat-value">
                    {successInfo.syncedCount}
                  </span>
                </li>
              </ul>

              <button
                type="button"
                className="gcal-primary-btn gcal-primary-btn-lg gcal-popup-cta"
                onClick={handleCloseSuccess}
              >
                <span>מצוין, סיימתי</span>
              </button>
            </div>
          </div>,
          document.body,
        )}

      {/* Revoked popup — shown when user removed our access from Google */}
      {revokedInfo &&
        createPortal(
          <div
            className="gcal-popup-overlay gcal-revoked-overlay"
            onClick={handleRevokedOverlayClick}
          >
            <div
              className="gcal-popup gcal-revoked-popup"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="gcal-popup-close-wrap">
                <ModalCloseButton onClick={handleCloseRevoked} />
              </div>

              <div className="gcal-revoked-icon-wrap">
                <WarningCircle size={64} weight="fill" />
              </div>

              <h3 className="gcal-popup-title">החיבור ליומן Google נותק</h3>
              <p className="gcal-popup-sub">
                ההרשאה ל-CliniKit הוסרה דרך חשבון Google
                {revokedInfo.previousEmail ? (
                  <>
                    {" "}(
                    <span dir="ltr">{revokedInfo.previousEmail}</span>
                    )
                  </>
                ) : null}
                . כדי שהפגישות ימשיכו להסתנכרן אוטומטית, צריך לחבר מחדש את
                החשבון.
              </p>

              <button
                type="button"
                className="gcal-primary-btn gcal-primary-btn-lg gcal-popup-cta"
                onClick={handleReconnectFromRevoked}
              >
                <img src={gLogo} alt="" className="gcal-btn-logo" />
                <span>חבר מחדש</span>
              </button>

              <button
                type="button"
                className="gcal-revoked-secondary"
                onClick={handleCloseRevoked}
              >
                לא עכשיו
              </button>
            </div>
          </div>,
          document.body,
        )}

      <ConfirmDialog
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        onConfirm={handleDisconnect}
        title="ניתוק יומן Google"
        message="פעולה זו תפסיק את הסנכרון האוטומטי של פגישות חדשות ליומן Google. אפשר לחבר מחדש בכל רגע. האם להמשיך?"
        confirmText="כן, נתק"
        cancelText="ביטול"
        type="danger"
        icon="warning"
      />
    </div>
  );
}

export default GoogleCalendarSync;
